import ComponentManager from "../../componentFramework";
import JsfAjaxStatus from "../../scripts/jsf/JsfAjaxStatus";
import {JsfAjaxRequestEvent} from "../../scripts/jsf/JsfAjax";
import EventManager from "../EventManager";

export interface ButtonEventMap {
	"click": MouseEvent;
}

@ComponentManager.register()
export default class Button<EventMap extends ButtonEventMap = ButtonEventMap> {
	public static componentStyleClass = "button";

	private static readonly disableStyleClasses: {[0]: string, [1]: string} = ["isDisabled", JsfAjaxStatus.isActiveStyleClass];

	private originalOnClick: ((this: GlobalEventHandlers, ev: MouseEvent) => any) | undefined | null;

	private readonly eventManager = new EventManager<EventMap>();
	public readonly addEventListener = this.eventManager.addEventListener;
	public readonly removeEventListener = this.eventManager.removeEventListener;

	constructor(private readonly element: HTMLElement,
	            private preventDefaultOnClick = false) {
	}

	@ComponentManager.on("ready")
	protected componentReady() {
		// Intercept onclick to see if button/link is disabled.
		// We can't use addEventListener because "onClick" attributes are
		// called before the event listeners are.
		this.originalOnClick = this.element.onclick;
		this.element.onclick = this.onClick;

		// Watch for ajax status updates
		JsfAjaxStatus.getInstance().addEventBeginListener(this.onJsfAjaxStatusEvent);
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		this.eventManager.destroy();
		JsfAjaxStatus.getInstance().removeEventBeginListener(this.onJsfAjaxStatusEvent);
		if (this.originalOnClick !== undefined) {
			this.element.onclick = this.originalOnClick;
			this.originalOnClick = null;
		}
	}

	private onJsfAjaxStatusEvent = (ev: JsfAjaxRequestEvent, addStatusElement: (elem: Element) => void, preventGlobalStatus: () => void) => {
		// If the source is a button, don't use the global indicator since we add our own indicator.
		// Unless this button is an "input" element since we can't style those automatically (we use a ::before)
		if (ev.sourceElement === this.element && !(this.element instanceof HTMLInputElement)) {
			preventGlobalStatus();
		}
	}

	public setPreventDefaultOnClick(preventDefaultOnClick: boolean) {
		this.preventDefaultOnClick = preventDefaultOnClick;
	}

	public click(): void {
		this.element.click();
	}

	private onClick = (ev: MouseEvent): boolean => {
		// Prevent the click action when the "button" is disabled.
		if (this.isElementDisabled()) {
			ev.preventDefault();
			ev.stopImmediatePropagation();
			return false;
		}

		this.eventManager.notifyEventListeners("click", ev);
		if (this.preventDefaultOnClick) {
			ev.preventDefault();
			return false; // Do not continue processing the event if we are preventing its default handling
		} else {
			// Otherwise call the original onclick handler (if there was one)
			return this.originalOnClick === null || this.originalOnClick!.call(this.element, ev); // this.originalOnClick will never be undefined here
		}
	}

	public setDisabled(isDisable: boolean) {
		if (this.element instanceof HTMLInputElement) {
			this.element.classList.toggle(Button.disableStyleClasses[0], isDisable);
		} else {
			if (isDisable) {
				this.element.setAttribute("disabled", "disabled");
			} else {
				this.element.removeAttribute("disabled");
			}
		}
	}

	private isElementDisabled(): boolean {
		if (this.element.hasAttribute("disabled")) {
			return true;
		}
		return this.element.classList.contains(Button.disableStyleClasses[0]) || this.element.classList.contains(Button.disableStyleClasses[1]);
	}
}

